<template>
  <panel title="User Usage">
    <div class="usage-container">
      <div class="chart-container">
        <chart-doughnut
          :options="chartOptions"
          :data="chartData"
        />
        <div class="gauge-center">
          <span class="count">{{ billableUserCount }}</span>
          <span class="label">/ {{ maxUsers }} active</span>
        </div>
      </div>
      <div class="axis-labels">
        <span>0</span>
        <span>{{ maxUsers }}</span>
      </div>
      <Button @click="addUser" class="tw:w-full tw:mt-2 tw:justify-center" severity="primary" size="small">
        <font-awesome-icon icon="user-plus" class="icon" /> Add User
      </Button>
      <Message v-if="showUpgradeAlert" class="upgrade-alert tw:mt-2" severity="error" closable @close="showUpgradeAlert = false">
        <font-awesome-icon icon="triangle-exclamation" class="icon" /> See your primary administrator to upgrade subscription
      </Message>
    </div>
  </panel>
</template>
<script>
import Panel from '@/components/Panel.vue'
import ChartDoughnut from '@/components/ChartDoughnut.vue'
import Button from 'primevue/button'
import Message from 'primevue/message'
import { mapGetters, mapState } from 'vuex'

const colors = {
  green: {
    backgroundColor: '#10b981',
    borderColor: '#059669'
  },
  yellow: {
    backgroundColor: '#f59e0b',
    borderColor: '#d97706'
  },
  red: {
    backgroundColor: '#f43f5e',
    borderColor: '#e11d48'
  }
}

function baseChartData () {
  return {
    labels: ['Active users', 'Unused user seats'],
    datasets: [{
      borderWidth: 1
    }]
  }
}

export default {
  components: {
    Panel,
    ChartDoughnut,
    Button,
    Message
  },
  computed: {
    ...mapState(['maxUsers', 'permissions']),
    ...mapGetters(['billableUserCount']),
    chartData () {
      const isDark = document.documentElement.classList.contains('app-dark')
      const grayBg = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
      const grayBorder = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'

      const chartData = baseChartData()
      const used = Math.min(this.billableUserCount, this.maxUsers)
      const unused = this.maxUsers - used
      const usedColor = unused === 0
        ? colors.red
        : (used >= unused * 9 ? colors.yellow : colors.green)

      Object.assign(chartData.datasets[0], {
        data: [used, unused],
        backgroundColor: [
          usedColor.backgroundColor,
          grayBg
        ],
        borderColor: [
          usedColor.borderColor,
          grayBorder
        ]
      })

      return chartData
    }
  },
  data () {
    return {
      chartOptions: {
        rotation: -90,
        circumference: 180,
        cutout: '78%',
        aspectRatio: 2,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true
          }
        },
        responsive: true,
        maintainAspectRatio: true
      },
      showUpgradeAlert: false
    }
  },
  methods: {
    addUser () {
      if (this.billableUserCount < this.maxUsers) {
        this.$router.push({ name: 'employee-list', params: { view: 'new' } })
      } else if (this.permissions.includes('manage_subscription')) {
        this.$router.push({ name: 'billing-subscription' })
      } else {
        this.showUpgradeAlert = true
      }
    }
  }
}
</script>
<style scoped>
.usage-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.chart-container {
  position: relative;
  width: 200px;
  max-width: 100%;
  margin: -5px auto 0 auto;
}

.gauge-center {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  pointer-events: none;
}

.gauge-center .count {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--color-slate-100);
  line-height: 1;
}

.gauge-center .label {
  font-size: 0.75rem;
  color: var(--color-slate-400);
  margin-top: 0.1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.axis-labels {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 200px;
  max-width: 100%;
  margin: 0 auto;
  border-top: 1px solid var(--color-slate-800);
  padding-top: 0.15rem;

  > span {
    font-size: .75rem;
    color: var(--color-slate-550);
    font-weight: bolder;
  }
}

.upgrade-alert {
  width: 100%;
}

.icon {
  margin-right: 3px;
}
</style>

